import Icon, { FileExcelOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Menu, message, Select, Space, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import companyApi from '../../../api/CompanyApi';
import hubApi from '../../../api/HubApi';
import memberApi from '../../../api/MemberApi';
import projectApi from '../../../api/ProjectApi';
import projectMemberApi from '../../../api/ProjectMemberApi';
import roleApi from '../../../api/RoleApi';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import SelectTableComponent from '../../../components/SelectTableComponent/SelectTableComponent';
import CustomContext from '../../../context/CustomContext';
import { Page } from '../../../model/elements';
import { Company, Hub, Member, Project, ProjectMember, Role, Service } from '../../../model/entities';
import { ServiceType } from '../../../model/types';
import { ReactComponent as CaretDownSvg } from '../../../resources/images/caret-down.svg';
import { ReactComponent as CloudUploadSvg } from '../../../resources/images/cloud-upload.svg';
import { ReactComponent as EditSvg } from '../../../resources/images/edit.svg';
import { ReactComponent as TrashSvg } from '../../../resources/images/trash-can.svg';
import automationService from '../../../services/AutomationService';
import notificationService from '../../../services/NotificationService';
import stringService from '../../../services/StringService';
import tableService from '../../../services/TableService';
import DeleteProjectMemberModal from './DeleteProjectMemberModal/DeleteProjectMemberModal';
import styles from './ProjectMembersPage.module.scss';
import UpdateCompanyModal from './UpdateCompanyModal/UpdateCompanyModal';
import UpdateCostCenterModal from './UpdateCostCenterModal/UpdateCostCenterModal';
import UpdateRolesModal from './UpdateRolesModal/UpdateRolesModal';

class ProjectMembersPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            display: 'grouped',
            hubs: [],
            projects: [],
            selectedProjectIds: [],
            selectedRowKeys: [],
            selectedProjectMembers: [],
            roles: [],
            companies: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        const { display } = this.state;
        try {
            this.setState({ loading: true });
            const hubs = await hubApi.list(true);
            this.setState({ hubs });

            // select hub and project
            if (this.props.location.search.includes('hubId')) {
                const params = new URLSearchParams(this.props.location.search);
                const hubId = params.get('hubId');
                const hub = hubs.find((p) => p.id === hubId);
                this.setState({ hub });

                if (hub) {
                    const responses = await Promise.all([
                        projectApi.list(hub.provider, hub.id),
                        companyApi.list(hub.id),
                    ]);
                    const projects = responses[0];
                    const companies = responses[1];
                    this.setState({ projects, companies });

                    if (this.props.location.search.includes('projectId')) {
                        const projectIds = params.getAll('projectId');
                        this.setState({ selectedProjectIds: projectIds });
                        await this.list(projectIds, display);
                    }
                }
            } else if (hubs.length > 0) {
                const hub = hubs[0];
                const responses = await Promise.all([projectApi.list(hub.provider, hub.id), companyApi.list(hub.id)]);
                const projects = responses[0];
                const companies = responses[1];

                this.setState({ hub, projects, companies });
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    list = async (selectedProjectIds: string[], display: DisplayType) => {
        const { hub, projectMembersPage, searchText } = this.state;

        // list project members
        const page = 0;
        const size = projectMembersPage?.size || tableService.pageSize;
        const sortField = projectMembersPage?.sort.field || 'lastName';
        const sortOrder = projectMembersPage?.sort.order || true;
        await this.listProjectMembers(
            page,
            size,
            sortField,
            sortOrder,
            display,
            hub!.id,
            selectedProjectIds,
            searchText,
        );

        // refresh roles
        if (hub && selectedProjectIds.length > 0) {
            const roles = (await roleApi.list(hub!.id, selectedProjectIds[0])) || [];
            this.setState({ roles });
        }
    };

    search = async (searchText: string) => {
        const { projectMembersPage, display, hub, selectedProjectIds } = this.state;
        try {
            this.setState({ loading: true, searchText });
            const page = 0;
            const size = projectMembersPage!.size;
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await this.listProjectMembers(
                page,
                size,
                sortField,
                sortOrder,
                display,
                hub!.id,
                selectedProjectIds,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { display, hub, selectedProjectIds, searchText } = this.state;
        try {
            this.setState({ loading: true });
            if (selectedProjectIds.length > 0) {
                const page = pagination.current! - 1;
                const size = pagination.pageSize!;
                const sortField = sorter.field;
                const sortOrder = sorter.order === 'ascend';
                await this.listProjectMembers(
                    page,
                    size,
                    sortField,
                    sortOrder,
                    display,
                    hub!.id,
                    selectedProjectIds,
                    searchText,
                );
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    listProjectMembers = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        display: DisplayType,
        hubId: string,
        selectedProjectIds: string[],
        searchText?: string,
    ) => {
        const projectMembersPage =
            selectedProjectIds.length > 0
                ? await projectMemberApi.list(
                      page,
                      size,
                      sortField,
                      sortOrder,
                      display === 'grouped',
                      hubId,
                      selectedProjectIds,
                      searchText,
                  )
                : undefined;
        this.setState({ projectMembersPage });
    };

    exportSpreadSheet = async () => {
        const { projectMembersPage, hub, selectedProjectIds, searchText, display } = this.state;
        try {
            this.setState({ exporting: true });
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await projectMemberApi.exportSpreadSheet(
                sortField,
                sortOrder,
                hub!.id,
                selectedProjectIds,
                display === 'grouped',
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ exporting: false });
        }
    };

    listProjects = async (hubId: string | undefined) => {
        const { hubs } = this.state;
        try {
            this.setState({ loading: true });
            const hub = hubs.find((p) => p.id === hubId);
            let projects: Project[] = [];
            let companies: Company[] = [];
            if (hub) {
                const responses = await Promise.all([projectApi.list(hub.provider, hub.id), companyApi.list(hub.id)]);
                projects = responses[0];
                companies = responses[1];
            }
            const selectedProjectIds: string[] = [];
            const projectMembersPage = undefined;
            this.setState({ hub, projects, companies, selectedProjectIds, projectMembersPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    changeProjects = async (selectedProjectIds: string[]) => {
        try {
            const { display } = this.state;
            this.setState({ loading: true, selectedProjectIds });
            this.unselectAll();
            await this.list(selectedProjectIds, display);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    changeDisplay = async (display: DisplayType) => {
        try {
            this.setState({ loading: true });
            const { selectedProjectIds } = this.state;
            this.setState({ display });
            await this.list(selectedProjectIds, display);
            this.unselectAll();
        } catch (error) {
            console.log(error);
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    select = (selectedRowKeys: Key[], selectedRows: ProjectMember[]) => {
        this.setState({ selectedRowKeys, selectedProjectMembers: selectedRows });
    };

    unselectAll = () => {
        this.setState({ selectedRowKeys: [], selectedProjectMembers: [] });
    };

    isUpdateCompaniesButtonDisabled = (): boolean => {
        return this.state.selectedProjectMembers.length === 0;
    };

    isUpdateRolesButtonDisabled = (): boolean => {
        return this.state.selectedProjectMembers.length === 0;
    };

    isUpdateCostCenterButtonDisabled = (): boolean => {
        return this.state.selectedProjectMembers.length === 0;
    };

    isDeleteButtonDisabled = (): boolean => {
        return this.state.selectedProjectMembers.length === 0;
    };

    showCompanyModal = (visible: boolean) => {
        const companyModalVisible = visible;
        this.setState({ companyModalVisible });
    };

    showRolesModal = (visible: boolean) => {
        const rolesModalVisible = visible;
        this.setState({ rolesModalVisible });
    };

    showCostCenterModal = (visible: boolean) => {
        const costCenterModalVisible = visible;
        this.setState({ costCenterModalVisible });
    };

    showDeleteModal = (visible: boolean) => {
        const deleteModalVisible = visible;
        this.setState({ deleteModalVisible });
    };

    updateCompany = async (values: any) => {
        const {
            hub,
            projects,
            selectedProjectIds,
            selectedProjectMembers,
            companies,
            display,
            projectMembersPage,
            searchText,
        } = this.state;
        try {
            // assign company and load project data
            let selectedCompany = companies.find((c) => c.id === values.company);
            const projectMembers = [...selectedProjectMembers].concat(
                selectedProjectMembers.flatMap((pm) => pm.siblings),
            );
            projectMembers.forEach((c) => (c.company = selectedCompany));
            projectMembers.forEach((pm) => this.loadProject(pm, projects));

            // update company
            const projectMembersWithStatus = await projectMemberApi.updateCompany(projectMembers, hub!.id);
            this.unselectAll();
            this.showCompanyModal(false);

            // display message
            if (projectMembersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }

            // refresh project members
            this.setState({ loading: true });
            const page = 0;
            const size = projectMembersPage?.size || tableService.pageSize;
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await this.listProjectMembers(
                page,
                size,
                sortField,
                sortOrder,
                display,
                hub!.id,
                selectedProjectIds,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    updateRoles = async (values: any) => {
        const {
            hub,
            projects,
            selectedProjectIds,
            selectedProjectMembers,
            projectMembersPage,
            roles,
            searchText,
            display,
        } = this.state;

        try {
            // assign roles and load project data
            let selectedRoles: Role[] = values.roles ? roles.filter((r) => values.roles.includes(r.id)) : [];
            const projectMembers = [...selectedProjectMembers].concat(
                selectedProjectMembers.flatMap((pm) => pm.siblings),
            );
            projectMembers.forEach((pm) => (pm.roles = selectedRoles));
            projectMembers.forEach((pm) => this.loadProject(pm, projects));

            // update roles
            const projectMembersWithStatus = await projectMemberApi.updateRoles(projectMembers, hub!.id);
            automationService.synchronizeProjectUserRoleServices(projectMembersWithStatus, hub!.id);
            this.unselectAll();
            this.showRolesModal(false);

            // display message
            if (projectMembersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }

            // refresh project members
            this.setState({ loading: true });
            const page = 0;
            const size = projectMembersPage?.size || tableService.pageSize;
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await this.listProjectMembers(
                page,
                size,
                sortField,
                sortOrder,
                display,
                hub!.id,
                selectedProjectIds,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    updateCostCenter = async (values: any) => {
        const { hub, selectedProjectIds, selectedProjectMembers, projectMembersPage, searchText, display } = this.state;
        try {
            // update cost center
            const members: Member[] = selectedProjectMembers.map((pm) => ({
                id: pm.id,
                email: pm.email,
                firstName: pm.firstName,
                lastName: pm.lastName,
                costCenter: values.costCenter,
            }));
            const membersWithStatus = await memberApi.updateCostCenter(members);
            this.unselectAll();
            this.showCostCenterModal(false);

            // display message
            if (membersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }

            // refresh project members
            this.setState({ loading: true });
            const page = 0;
            const size = projectMembersPage?.size || tableService.pageSize;
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await this.listProjectMembers(
                page,
                size,
                sortField,
                sortOrder,
                display,
                hub!.id,
                selectedProjectIds,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    /**
     * Deletes project members from the selected projects.
     */
    delete = async () => {
        const { hub, projects, selectedProjectIds, selectedProjectMembers, display, projectMembersPage, searchText } =
            this.state;
        try {
            // load project data
            const projectMembers = [...selectedProjectMembers].concat(
                selectedProjectMembers.flatMap((pm) => pm.siblings),
            );
            projectMembers.forEach((pm) => this.loadProject(pm, projects));

            // delete project members
            const projectMembersWithStatus = await projectMemberApi.delete(projectMembers, hub!.id);
            this.unselectAll();
            this.showDeleteModal(false);

            // display message
            if (projectMembersWithStatus.every((m) => m.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.removed' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.removedWithErrors' }), 5);
            }

            // refresh project members
            this.setState({ loading: true });
            const page = 0;
            const size = projectMembersPage?.size || tableService.pageSize;
            const sortField = projectMembersPage!.sort.field!;
            const sortOrder = projectMembersPage!.sort.order!;
            await this.listProjectMembers(
                page,
                size,
                sortField,
                sortOrder,
                display,
                hub!.id,
                selectedProjectIds,
                searchText,
            );
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    /**
     * Loads the project data (name) of a project member.
     * @param projectMember - the project member
     * @param projects - the projects
     */
    loadProject = (projectMember: ProjectMember, projects: Project[]) => {
        const project = projects.find((p) => projectMember.project && p.id === projectMember.project.id);
        if (projectMember.project && project) {
            projectMember.project.name = project.name;
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSelector()}
                {this.renderToolbar()}
                {this.renderTable()}
                {this.renderCompanyModal()}
                {this.renderRolesModal()}
                {this.renderCostCenterModal()}
                {this.renderDeleteModal()}
            </>
        );
    };

    renderSelector = (): React.ReactElement | undefined => {
        const { hubs, hub, projects, selectedProjectIds, display } = this.state;
        const hubOptions = hubs.map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {`${hub.provider} > ${hub.name}`}
            </Select.Option>
        ));
        const projectOptions = projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));

        return (
            <div className={styles.selector}>
                <label>
                    <FormattedMessage id="projectMembers.hub" />
                </label>
                <Select
                    size="large"
                    allowClear
                    className={styles.hubs}
                    value={hub?.id}
                    onChange={this.listProjects}
                    placeholder={<FormattedMessage id="projectMembers.hub.placeholder" />}
                >
                    {hubOptions}
                </Select>
                <label>
                    <FormattedMessage id="projectMembers.projects" />
                </label>
                <Select
                    size="large"
                    allowClear
                    className={styles.projects}
                    value={selectedProjectIds}
                    onChange={this.changeProjects}
                    placeholder={<FormattedMessage id="projectMembers.projects.placeholder" />}
                    showSearch
                    filterOption={stringService.filterOptions}
                    mode={'multiple'}
                >
                    {projectOptions}
                </Select>
                <label>
                    <FormattedMessage id="projectMembers.display" />
                </label>
                <Select
                    size="large"
                    allowClear
                    className={styles.display}
                    value={display}
                    onChange={this.changeDisplay}
                >
                    <Select.Option key="grouped" value="grouped">
                        <FormattedMessage id="projectMembers.display.grouped" />
                    </Select.Option>
                    <Select.Option key="all" value="all">
                        <FormattedMessage id="projectMembers.display.all" />
                    </Select.Option>
                </Select>
                <Divider />
            </div>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const { hub, selectedProjectIds, exporting } = this.state;
        const placeholder: string = intl.formatMessage({ id: 'projectMembers.search' });
        const disabled = selectedProjectIds.length === 0;
        const projectIdsAsParam = selectedProjectIds.map((id) => 'projectId=' + id).join('&');

        return (
            <div className="toolbar">
                <Search
                    placeholder={placeholder}
                    onSearch={this.search}
                    size="large"
                    className="search"
                    disabled={disabled}
                />
                <Space>
                    <Tooltip title={<FormattedMessage id="projectMembers.exportSpreadSheet" />}>
                        <Button
                            size="large"
                            icon={<FileExcelOutlined />}
                            onClick={this.exportSpreadSheet}
                            loading={exporting}
                            disabled={disabled}
                        ></Button>
                    </Tooltip>
                    <Link to={`/setup/project-members/import/${hub?.provider}/${hub?.id}?${projectIdsAsParam}`}>
                        <Tooltip title={<FormattedMessage id="projectMembers.import" />}>
                            <Button
                                size="large"
                                icon={<Icon component={CloudUploadSvg} />}
                                disabled={disabled}
                            ></Button>
                        </Tooltip>
                    </Link>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={disabled}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="company"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showCompanyModal(true)}
                                    disabled={this.isUpdateCompaniesButtonDisabled()}
                                >
                                    <FormattedMessage id="projectMembers.updateCompany" />
                                </Menu.Item>
                                <Menu.Item
                                    key="roles"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showRolesModal(true)}
                                    disabled={this.isUpdateRolesButtonDisabled()}
                                >
                                    <FormattedMessage id="projectMembers.updateRoles" />
                                </Menu.Item>
                                <Menu.Item
                                    key="costCenter"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showCostCenterModal(true)}
                                    disabled={this.isUpdateCostCenterButtonDisabled()}
                                    hidden={!this.context.auth?.authorities.includes('ROLE_ADMIN')}
                                >
                                    <FormattedMessage id="projectMembers.updateCostCenter" />
                                </Menu.Item>
                                <Menu.Item
                                    key="delete"
                                    icon={<Icon component={TrashSvg} />}
                                    onClick={() => this.showDeleteModal(true)}
                                    disabled={this.isDeleteButtonDisabled()}
                                    hidden={!this.context.auth?.authorities.includes('ROLE_ADMIN')}
                                >
                                    <FormattedMessage id="projectMembers.delete" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderCompanyModal = (): React.ReactElement | undefined => {
        const { companyModalVisible, companies } = this.state;

        return (
            <UpdateCompanyModal
                companies={companies}
                visible={companyModalVisible}
                onUpdate={this.updateCompany}
                onCancel={() => this.showCompanyModal(false)}
            />
        );
    };

    renderRolesModal = (): React.ReactElement | undefined => {
        const { rolesModalVisible, roles } = this.state;

        return (
            <UpdateRolesModal
                roles={roles}
                visible={rolesModalVisible}
                onUpdate={this.updateRoles}
                onCancel={() => this.showRolesModal(false)}
            />
        );
    };

    renderCostCenterModal = (): React.ReactElement | undefined => {
        const { costCenterModalVisible } = this.state;

        return (
            <UpdateCostCenterModal
                visible={costCenterModalVisible}
                onUpdate={this.updateCostCenter}
                onCancel={() => this.showCostCenterModal(false)}
            />
        );
    };

    renderDeleteModal = (): React.ReactElement | undefined => {
        const { deleteModalVisible } = this.state;

        return (
            <DeleteProjectMemberModal
                visible={deleteModalVisible}
                onDelete={this.delete}
                onCancel={() => this.showDeleteModal(false)}
            />
        );
    };

    renderTable = (): React.ReactElement | undefined => {
        const { display, projectMembersPage, selectedProjectMembers, selectedRowKeys, loading } = this.state;
        const items = projectMembersPage ? projectMembersPage.content : [];
        const columns: ColumnsType<ProjectMember> = [
            {
                title: <FormattedMessage id="projectMember.lastName" />,
                dataIndex: 'lastName',
                key: 'lastName',
                sorter: true,
                defaultSortOrder: 'ascend',
            },
            {
                title: <FormattedMessage id="projectMember.firstName" />,
                dataIndex: 'firstName',
                key: 'firstName',
                sorter: true,
            },
            {
                title: <FormattedMessage id="projectMember.email" />,
                dataIndex: 'email',
                key: 'email',
                sorter: true,
            },
            {
                title: <FormattedMessage id="projectMember.company" />,
                dataIndex: 'company',
                key: 'company',
                sorter: true,
                width: 180,
                render: (value, item) => this.renderCompanies(item),
            },
            {
                title: <FormattedMessage id="projectMember.roles" />,
                dataIndex: 'roles',
                key: 'roles',
                sorter: true,
                width: 180,
                render: (value, item) => this.renderRoles(item),
            },
            {
                title: <FormattedMessage id="projectMember.services.projectAdministration" />,
                dataIndex: 'services.projectAdministration',
                key: 'services.projectAdministration',
                align: 'center',
                sorter: true,
                width: 160,
                render: (services, item) => this.renderServiceValue(item.services, 'projectAdministration'),
            },
            {
                title: <FormattedMessage id="projectMember.services.documentManagement" />,
                dataIndex: 'services.documentManagement',
                key: 'services.documentManagement',
                align: 'center',
                sorter: true,
                width: 50,
                render: (services, item) => this.renderServiceValue(item.services, 'documentManagement'),
            },
            {
                title: <FormattedMessage id="projectMember.services.projectManagement" />,
                dataIndex: 'services.projectManagement',
                key: 'services.projectManagement',
                align: 'center',
                sorter: true,
                width: 50,
                render: (services, item) => this.renderServiceValue(item.services, 'projectManagement'),
            },
            {
                title: <FormattedMessage id="projectMember.services.modelCoordination" />,
                dataIndex: 'services.modelCoordination',
                key: 'services.modelCoordination',
                align: 'center',
                sorter: true,
                width: 50,
                render: (services, item) => this.renderServiceValue(item.services, 'modelCoordination'),
            },
            {
                title: <FormattedMessage id="projectMember.services.fieldManagement" />,
                dataIndex: 'services.fieldManagement',
                key: 'services.fieldManagement',
                align: 'center',
                sorter: true,
                width: 50,
                render: (services, item) => this.renderServiceValue(item.services, 'fieldManagement'),
            },
        ];

        // add cost center column if user is admin
        if (this.context.auth?.authorities.includes('ROLE_ADMIN')) {
            columns.push({
                title: <FormattedMessage id="projectMember.costCenter" />,
                dataIndex: 'costCenter',
                key: 'costCenter',
                align: 'center',
                sorter: true,
                width: 130,
            });
        }

        // add project column if display all
        if (display === 'all') {
            columns.unshift({
                title: <FormattedMessage id="projectMember.project" />,
                dataIndex: 'project',
                key: 'project',
                sorter: true,
                defaultSortOrder: 'ascend',
                width: 180,
                render: (project) => project.name,
            });
        }

        let rowSelection: TableRowSelection<ProjectMember> | undefined;
        rowSelection = {
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: this.select,
        };

        return (
            <SelectTableComponent
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(projectMembersPage)}
                rowKey={(item) => `${item.email}-${item.project.id}`}
                onChange={this.paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
                rowSelection={rowSelection}
                selectedItems={selectedProjectMembers.map((projectMember) => projectMember.email)}
                onUnselectAll={this.unselectAll}
            />
        );
    };

    renderCompanies = (projectMember: ProjectMember): string => {
        return [projectMember.company]
            .concat(projectMember.siblings.map((pm) => pm.company))
            .filter((c) => c && c.name)
            .map((c) => c!.name)
            .filter((item, index, self) => self.indexOf(item) === index)
            .sort((a, b) => a.localeCompare(b))
            .join(', ');
    };

    renderRoles = (projectMember: ProjectMember): string => {
        return [...projectMember.roles]
            .concat(projectMember.siblings.flatMap((pm) => pm.roles))
            .filter((c) => c && c.name)
            .map((c) => c!.name)
            .filter((item, index, self) => self.indexOf(item) === index)
            .sort((a, b) => a.localeCompare(b))
            .join(', ');
    };

    renderServiceValue = (services: Service[], serviceType: ServiceType) => {
        const servicesByType = services.filter((s) => s.name === serviceType);
        const service = servicesByType.length > 0 ? servicesByType[0] : undefined;
        return service && service.access !== 'none' ? <FormattedMessage id={service.access} /> : '-';
    };

    render() {
        return <LayoutComponent pageId="projectMembers">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(ProjectMembersPage);

interface Props extends WrappedComponentProps, RouteComponentProps {}

interface State {
    hubs: Hub[];
    hub?: Hub;
    projects: Project[];
    selectedProjectIds: string[];
    projectMembersPage?: Page<ProjectMember>;
    display: DisplayType;
    searchText?: string;
    loading?: boolean;
    exporting?: boolean;
    saving?: boolean;
    selectedRowKeys: Key[];
    selectedProjectMembers: ProjectMember[];
    companyModalVisible?: boolean;
    rolesModalVisible?: boolean;
    costCenterModalVisible?: boolean;
    deleteModalVisible?: boolean;
    roles: Role[];
    companies: Company[];
}

type DisplayType = 'grouped' | 'all';
